import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { X, Upload, Loader2, ArrowLeft, Download, Check, Copy } from "lucide-react";
import IntegrationConfigDialog from "../../Components/Common/IntegrationSetConfigDialog";
import Upcoming from "../../Components/Common/Upcoming";
import { Drawer, DrawerClose, DrawerContent } from "../../Components/UI/drawer";
import useIsMobile from "../../Hooks/useIsMobile";
import { DatePicker } from "../../Components/Common/DatePicker";
import { toast } from "sonner";
import { getIntegrationData } from "./ThreadsAPI";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../Components/UI/table";
import { Card, CardContent } from "../../Components/UI/card";
import { dateDiff } from "../../Utils/Utilities";
import { getConfigData } from "../../Utils/Storage";
import { FaDownload, FaLeftLong, FaUpload } from "react-icons/fa6";
import ImportThreadsExcel from "./ImportThreadsExcel";
import MetaLeadImport from "./Integrations/MetaLeadImport";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "../../Components/UI/tooltip";
import { useSelector } from "react-redux";
import { SHOW_IMPORT_INQUIRIES, canShowImportInquiries } from "../../Utils/FeatureFlags";
import IntgnConfig from "../Settings/SubSettings/IntgnConfig";
import FieldWithIcon from "../../Components/Common/FieldWithIcon";

const integrations = {
    pull: [
        { name: "IndiaMart", logo: "/assets/Integration/indiamart.png", type: "pull" },
        { name: "TradeIndia", logo: "/assets/Integration/tradeindia.png" , type: "pull"},
        { name: "Meta", logo: "/assets/Integration/meta.png" , type: "pull"},
        // { name: "Housing", logo: "/assets/Integration/housing.png" },
        // { name: "99acres", logo: "/assets/Integration/99acres.png" },
        { name: "Excel", logo: "/assets/Integration/SCExcel.png" , type: "pull"},
    ],
    push: [
        { name: "IndiaMart", logo: "/assets/Integration/indiamart.png", type: "push" },
        { name: "Meta", logo: "/assets/Integration/meta.png", type: "push" },
        { name: "Justdial", logo: "/assets/Integration/justdial.png", type: "push" },
        // { name: "MagicBricks", logo: "/assets/Integration/magicbricks.png" },
        // { name: "Website", logo: "/assets/Integration/w3.png" },
    ],
};

const IntegrationCard = ({ name, logo, onClick, isLive }) => (
    <div
        onClick={onClick}
        className="image-icons"
    >
        <img
            src={isLive() ? `/Web${logo}` : logo}
            alt={name}
            className="max-h-full max-w-full object-contain" />
    </div>
);

export default function Integrations({
    triggerButton = true,
    triggerText = "Import Inquiries",
    buttonText = "Import",
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    triggerClassName = "",
    threadInfo
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [dateDialogOpen, setDateDialogOpen] = useState(false);
    const [upcomingDialogOpen, setUpcomingDialogOpen] = useState(false);
    const [threadExcelDialogOpen, setThreadExcelDialogOpen] = useState(false);
    const [metaLeadImportOpen, setMetaLeadImportOpen] = useState(false);
    const [indiaMartPushOpen, setIndiaMartPushOpen] = useState(false);
    const [configExists, setConfigExists] = useState(true);
    const [configDialogType, setConfigDialogType] = useState(null); // "integration", "justdial"
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setIsOpen = controlledOnOpenChange || setInternalOpen;
    const isMobile = useIsMobile()
    const onButtonClick = () => setIsOpen(true)

    const getConfigFromStorage = (intgnCode) => {
        try {
            const { EIM, ETI } = getConfigData();
            if (intgnCode === "IMRT") {
                return (EIM && Number(EIM) === 2) || false;
            } else if (intgnCode === "TI") {
                return (ETI && Number(ETI) === 2) || false;
            }
            return true;
        } catch (error) {
            console.error("Error getting config from storage:", error);
            return false;
        }
    };

    const getIntegrationCode = (intgnName) => {
        const codeMap = {
            "IndiaMart": "IMRT",
            "TradeIndia": "TI"
        };
        return codeMap[intgnName] || null;
    };

    useEffect(() => {
        if (isOpen) {
            setSelectedIntegration(null);
            setConfigDialogOpen(false);
        }
    }, [isOpen]);

    const handleIntegrationClick = (integration) => {
        setSelectedIntegration(integration.name);
        if ((integration.name === "IndiaMart" || integration.name === "TradeIndia") && integration.type === "pull") {
            const intgnCode = getIntegrationCode(integration.name);
            const hasConfig = getConfigFromStorage(intgnCode);
            setConfigExists(hasConfig);

            if (hasConfig) {
                setDateDialogOpen(true);
            } else {
                setConfigDialogType("integration");
                setConfigDialogOpen(true);
            }
        } else if (integration.name === "IndiaMart" && integration.type === "push") {
            setIndiaMartPushOpen(true);
        } else if (integration.name === "Excel") {
            if (isMobile) {
                toast.error("Excel import is not supported on mobile devices.");
                return;
            }
            // Open thread excel import dialog
            setThreadExcelDialogOpen(true);
        } else if (integration.name === "Meta") {
            setMetaLeadImportOpen(true);
        } else if (integration.name === "Justdial") {
            setConfigDialogType("justdial");
            setConfigDialogOpen(true);
        }
        else {
            setUpcomingDialogOpen(true);
        }
    };
    const isLive = () => {
        const hostname = window.location.hostname.toLowerCase();
        const pathname = window.location.pathname.toLowerCase();
        return hostname.includes("sahicrm.com") || pathname.startsWith("/web");
    };
    const onCloseDialog = () => {
        setIsOpen(false)
    }
    const IntegrationBody = () => {
        const pullIntegrations = isMobile
            ? integrations.pull.filter((item) => item.name !== "Excel")
            : integrations.pull;

        return (
            <div className="pt-4 lg:pt-0 px-4 pb- space-y-4 max-w-4xl ">
                <section>
                    <h2 className="text-lg text-sahi-primary-color font-semibold ">
                        Pull Integrations
                        <span className="subline-text italic ">
                            {" "}Click below to fetch leads from these sources.
                        </span>
                    </h2>
                    <div className="mt-4 grid grid-cols-3 gap-4">
                        {pullIntegrations.map((item) => (
                            <IntegrationCard
                                key={item.name}
                                {...item}
                                onClick={() => handleIntegrationClick(item)}
                                isLive={isLive}
                            />
                        ))}
                    </div>
                </section>
                <section>
                    <h2 className="text-lg text-sahi-primary-color  font-semibold">
                        Push Integrations
                        <span className="subline-text italic">
                            {" "} Leads from these sources will be directly fetched and imported.
                        </span>
                    </h2>
                    <div className="mt-4 grid grid-cols-3 gap-4">
                        {integrations.push.map((item) => (
                            <IntegrationCard
                                key={item.name}
                                {...item}
                                onClick={() => handleIntegrationClick(item)}
                                isLive={isLive}
                            />
                        ))}
                    </div>
                </section>
            </div>
        )
    }

    if (!canShowImportInquiries()) return null;

    return (
        <>
            {triggerButton && (
                isMobile ? (
                    <Button
                        variant="ghost"
                        size="default"
                        className="p-0 w-auto h-auto"
                        onClick={onButtonClick}
                    >
                        <Download className="!h-[20px] !w-[20px] " />
                    </Button>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button onClick={onButtonClick} variant="additional" >
                                <Download className={`h-4 w-4 ${triggerClassName}`} />
                                <p>{buttonText}</p>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Import Inquiries from B2B Platforms</p>
                        </TooltipContent>
                    </Tooltip>
                )

            )}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="w-[580px] h-full p-0 m-0">
                    {isMobile ? (
                        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                            <div className="sm:hidden flex items-center justify-between w-full h-8">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                        <ArrowLeft className="h-6 w-6" />
                                        <span className="sr-only">Back</span>
                                    </DialogClose>
                                    <DialogTitle className="text-lg font-medium">
                                        <div className="truncate">
                                            {triggerText}
                                        </div>
                                    </DialogTitle>
                                </div>
                            </div>
                        </DialogHeader>
                    ) : (
                        <DialogHeader className="hidden lg:flex  items-center  gap-2 py-3 h-[62px] flex-row !justify-between">
                            <div className="flex items-center gap-2">
                                <DialogTitle className="text-lg font-semibold">{triggerText}</DialogTitle>
                            </div>
                            <DialogClose className=" p-1 sahi-radius">
                                <X className="h-5 w-5" />
                            </DialogClose>
                        </DialogHeader>
                    )}

                    <IntegrationBody />
                    <div className="w-full lg:w-fit !sahi-radius mb-4 mx-4 mt-1 lg:mt-0">
                        <Button
                            onClick={onCloseDialog}
                            variant="secondary"
                            className="!sahi-radius w-fit cls-btn"
                        >
                            <X className="h-4 w-4" />
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <IntegrationConfigDialog
                open={configDialogOpen}
                onOpenChange={setConfigDialogOpen}
                triggerText={selectedIntegration}
                threadInfo={threadInfo}
            />

            <IntegrationImporter
                open={dateDialogOpen && configExists}
                onOpenChange={setDateDialogOpen}
                triggerText={selectedIntegration}
                threadInfo={threadInfo}
                configExists={configExists}
            />

            <Upcoming
                open={upcomingDialogOpen}
                onOpenChange={setUpcomingDialogOpen}
            />

            <ImportThreadsExcel
                open={threadExcelDialogOpen}
                onClose={() => {
                    setThreadExcelDialogOpen(false);
                }}
                threadInfo={threadInfo}
            />

            <MetaLeadImport
                open={metaLeadImportOpen}
                onOpenChange={(open) => setMetaLeadImportOpen(open)}
            />

            {configDialogOpen && configDialogType === "integration" && (
                <IntgnConfig
                    intgnName={selectedIntegration?.toLowerCase() === "indiamart" ? "indiaMart" : "tradeIndia"}
                    isWebEdit={false}
                    onClose={() => {
                        setConfigDialogOpen(false);
                        setConfigDialogType(null);
                    }}
                    preventOutsideClick={false}
                />
            )}

            {configDialogOpen && configDialogType === "justdial" && (
                <JustDialConfigDialog
                    open={configDialogOpen}
                    onOpenChange={(open) => {
                        setConfigDialogOpen(open);
                        setConfigDialogType(null);
                        if (!open) setIsOpen(true);
                    }}
                />
            )}

            <IndiaMartPushConfigDialog
                open={indiaMartPushOpen}
                onOpenChange={(open) => {
                    setIndiaMartPushOpen(open);
                    if (!open) setIsOpen(true);
                }}
                isMobile={isMobile}
            />
        </>
    );
}

function IntegrationImporter({ open, onOpenChange, triggerText, threadInfo, configExists }) {
    const [fromDT, setFromDT] = useState(new Date());
    const [toDT, setToDT] = useState(new Date());
    const [errorDialogData, setErrorDialogData] = useState([]);
    const isMobile = useIsMobile();
    const [activeDialog, setActiveDialog] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currIntgn, setCurrIntgn] = useState("")

    useEffect(() => {
        if (open) {
            setActiveDialog("importer");
            const today = new Date();
            setFromDT(today);
            setToDT(today);
        }
    }, [open]);

    useEffect(() => {
        setCurrIntgn(triggerText)
    }, [triggerText])

    const onCloseDialog = () => {
        onOpenChange(!open)
    }

    const validateDateRange = (fromDT, toDT, currIntgn) => {
        const maxDays = {
            IndiaMart: 7,
        };

        const limit = maxDays[currIntgn?.toLowerCase()] ?? maxDays.default;

        if (fromDT > toDT) {
            toast.error("Please select valid dates");
            return false;
        }

        if (dateDiff(fromDT, toDT, "DD") > limit) {
            toast.error(`Please select a date range of ${limit} days or less.`);
            return false;
        }

        return true;
    };

    const onCloseErr = () => {
        setActiveDialog("importer");
    }

    const handleImportClick = async () => {
        const curr = currIntgn?.toLowerCase();
        let finalFromDT = fromDT;
        let finalToDT = toDT;

        if (curr === "indiamart") {
            if (!validateDateRange(finalFromDT, finalToDT, currIntgn))
                return;
        } else if (curr === "tradeindia") {
            const baseFromDT = new Date(finalFromDT);
            baseFromDT.setHours(0, 0, 0, 0);

            finalFromDT = new Date(baseFromDT);
            finalToDT = new Date(baseFromDT);

            const isToday = baseFromDT.getTime() === new Date().setHours(0, 0, 0, 0);

            if (!isToday) {
                finalToDT.setDate(finalToDT.getDate() + 1);
            }
        }

        setIsLoading(true);
        try {
            const retIntgnBulkRes = await getIntegrationData({
                caseType: curr,
                fromDT: finalFromDT,
                toDT: finalToDT,
            });

            if (retIntgnBulkRes?.length) {
                setErrorDialogData(retIntgnBulkRes);
                setActiveDialog("error");
            }
        } catch (err) {
            toast.error("Failed to import data.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const IndiaMartComponent = () => {
        return (
            <>
                <p className="subline-text  !text-black  font-semibold !text-sm !ml-0">
                    Select a period of upto 1 week to import Inquiries.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <DatePicker
                        value={fromDT}
                        onChange={setFromDT}
                        label="From"
                        fieldID="fromDT"
                        param="noFuture"
                        maxDate={toDT}
                    />
                    <DatePicker
                        value={toDT}
                        onChange={setToDT}
                        label="To"
                        fieldID="toDT"
                        param="noFuture"
                        minDate={fromDT}
                    />
                </div>
            </>
        )
    }

    const TradeIndiaComponent = () => {
        return (
            <>
                <p className="subline-text !text-text-color font-semibold !text-base !ml-0">
                    Select a date to import Inquiries.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <DatePicker
                        value={fromDT}
                        label="Date"
                        onChange={setFromDT}
                        fieldID="fromDT"
                        param="noFuture"
                    />
                </div>
            </>
        )
    }

    const MidBody = () => {
        if (!configExists) {
            return null;
        }

        return (
            <div className="pt-0 lg:pt-0 px-4 pb-4 space-y-4 text-sm">

                {currIntgn === "IndiaMart" && <IndiaMartComponent />}
                {currIntgn === "TradeIndia" && <TradeIndiaComponent />}

                <div className="flex flex-row items-stretch gap-3 w-full ">
                    <Button
                        onClick={handleImportClick}
                        className="w-1/2 lg:w-fit flex items-center justify-center"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="animate-spin h-4 w-4" />
                                <span>Importing...</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Download className="h-4 w-4" />
                                <span>Import</span>
                            </div>
                        )}
                    </Button>
                    <div className="w-1/2 lg:w-fit !sahi-radius">

                        <Button
                            variant="secondary"
                            disabled={isLoading}
                            onClick={onCloseDialog}
                            className="!w-full lg:w-fit cls-btn"
                        >
                            <X className="h-4 w-4" />
                            Close
                        </Button>
                    </div>

                </div>
            </div>
        );
    };

    return (
        <>
            {activeDialog === "importer" && (
                <Dialog open={open} onOpenChange={onOpenChange}>
                    <DialogContent className="sm:w-[450px] p-0 overflow-hidden sahi-radius">
                        {isMobile ? (
                            <DialogHeader className="block lg:hidden dialog-header !justify-start !gap-2 text-lg ">
                                <DialogClose className=" p-1 sahi-radius">
                                    <ArrowLeft className="h-5 w-5" />
                                </DialogClose>
                                <div className="flex items-center gap-2">
                                    <DialogTitle className="text-lg font-semibold">{`Import - ${triggerText}`}</DialogTitle>
                                </div>
                            </DialogHeader>
                        ) : (
                            <DialogHeader className="hidden lg:flex  items-center  gap-2 py-3 h-[62px] flex-row !justify-between">
                                <div className="flex items-center gap-2">
                                    <DialogTitle className="text-lg font-semibold">{`Import - ${triggerText}`}</DialogTitle>
                                </div>
                                <DialogClose className=" p-1 sahi-radius">
                                    <X className="h-5 w-5" />
                                </DialogClose>
                            </DialogHeader>
                        )}
                        <MidBody />
                    </DialogContent>
                </Dialog>
            )}

            {activeDialog === "error" && (
                <Dialog
                    open={activeDialog === "error"}
                    onOpenChange={(e) => {
                        if (!e) setActiveDialog("importer");
                    }}
                >
                    <DialogContent className="w-full max-w-6xl pb-4 sahi-radius overflow-hidden">
                        {isMobile ? (
                            <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
                                <DialogClose className=" p-1 sahi-radius">
                                    <ArrowLeft className="h-5 w-5" />
                                </DialogClose>

                                <div className="flex items-center gap-2">
                                    <DialogTitle className="font-semibold text-lg">
                                        {`Import ${errorDialogData.length === 1 ? "Error" : "Errors"}`}
                                    </DialogTitle>
                                </div>
                            </DialogHeader>
                        ) : (
                            <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
                                <div className="flex items-center gap-2">
                                    <DialogTitle className="font-semibold text-lg">
                                        {`Import ${errorDialogData.length === 1 ? "Error" : "Errors"}`}
                                    </DialogTitle>
                                </div>

                                <DialogClose className="p-1 sahi-radius">
                                    <X className="h-5 w-5" />
                                </DialogClose>
                            </DialogHeader>
                        )}


                        <div className="px-4 ">
                            <Card className="plain" >
                                <CardContent className="p-0">
                                    <div className="max-h-[calc(100vh-110px)] lg:max-h-[60vh] overflow-auto">
                                        <Table className="min-w-full table-auto">
                                            <TableHeader >
                                                <TableRow>
                                                    <TableHead className="w-[40px]">No.</TableHead>
                                                    <TableHead>Contact</TableHead>
                                                    <TableHead>Errors</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {errorDialogData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3} className="text-center py-8">
                                                            Empty data.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    errorDialogData.map((entry, index) => (
                                                        <TableRow key={index} className="align-top">
                                                            <TableCell className="w-[40px] px-4 align-top">{index + 1}</TableCell>
                                                            <TableCell className="align-top px-4">{entry.BizName || "-"}</TableCell>
                                                            <TableCell className="!text-sahi-red align-top px-4">
                                                                {[...new Set((entry.ErrMsg || "Unknown error")
                                                                    .split(".")
                                                                    .map(s => s.trim())
                                                                    .filter(Boolean)
                                                                )].map((msg, i) => (
                                                                    <div key={i}>• {msg}.</div>
                                                                ))}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="w-fit !sahi-radius px-4">

                            <Button
                                variant="secondary"
                                disabled={isLoading}
                                onClick={onCloseErr}
                                className="!w-full lg:w-fit cls-btn"
                            >
                                <X className="h-4 w-4" />
                                Close
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

function IndiaMartPushConfigDialog({ open, onOpenChange, isMobile }) {
    const { data: agencyData } = useSelector(state => state.agencyData) || {};
    const adminMobile = agencyData?.Mobile || "";
    const adminCC = agencyData?.CC || "";

    const pushUrl = `https://sahicrm.com/External.asmx/setIMLead?mob=${adminCC}_${adminMobile}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange} preventOutsideClick>
            <DialogContent className="sm:max-w-[540px] w-full">

                {isMobile ? (
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-6 w-6" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium">
                                    <div className="truncate">
                                        Configuration – IndiaMart
                                    </div>
                                </DialogTitle>
                            </div>
                        </div>
                    </DialogHeader>
                ) : (
                    <DialogHeader className="hidden lg:flex  items-center  gap-2 py-3 h-[62px] flex-row !justify-between">
                        <div className="flex items-center gap-2">
                            <DialogTitle className="text-lg font-semibold">Configuration – IndiaMart</DialogTitle>
                        </div>
                        <DialogClose className=" p-1 sahi-radius">
                            <X className="h-5 w-5" />
                        </DialogClose>
                    </DialogHeader>
                )}

                <div className="px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0 space-y-2">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-foreground font-extrabold">
                                Auto Inquiry Delivery (PUSH)
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                            Make sure your IndiaMART plan supports real-time inquiry delivery. Share the link below with support to enable push integration.
                        </p>

                        <FieldWithIcon
                            key={'indiaMartPush'}
                            fieldID={'indiaMartPush'}
                            label={''}
                            value={pushUrl}
                            iconTypes={['copy']}
                            disabled={true}
                            readOnly={true}
                        />
                    </div>

                    <div className="py-2 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3">
                        <Button
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            className="w-full sm:w-auto"
                        >
                            <X className="w-3 h-3 mr-1" />
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// JustDial Configuration Dialog Component
function JustDialConfigDialog({ open, onOpenChange }) {
    const { data: agencyData } = useSelector(state => state.agencyData) || {};
    const isMobile = useIsMobile();
    const adminMobile = agencyData?.Mobile || "";
    const adminCC = agencyData?.CC || "";

    const pushUrl = `https://sahicrm.com/Int.asmx/SetJDInquiry?mob=${adminCC}_${adminMobile}`;

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(pushUrl);
        toast.success("URL copied to clipboard!");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange} preventOutsideClick>
            <DialogContent className="sm:max-w-[540px] w-full">
                 {isMobile ? (
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-6 w-6" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium">
                                    <div className="truncate">
                                        Configuration – JustDial
                                    </div>
                                </DialogTitle>
                            </div>
                        </div>
                    </DialogHeader>
                ) : (
                    <DialogHeader className="hidden lg:flex  items-center  gap-2 py-3 h-[62px] flex-row !justify-between">
                        <div className="flex items-center gap-2">
                            <DialogTitle className="text-lg font-semibold">Configuration – JustDial</DialogTitle>
                        </div>
                        <DialogClose className=" p-1 sahi-radius">
                            <X className="h-5 w-5" />
                        </DialogClose>
                    </DialogHeader>
                )}

                <div className="px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0 space-y-2">
                    {/* Auto Inquiry Delivery Section */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-foreground font-extrabold">
                                Auto Inquiry Delivery (PUSH)
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">
                            JustDial can call our API whenever a new inquiry is received. Please share the following link with the JustDial Support team to enable this functionality.
                        </p>

                        {/* Push URL Field */}
                        {/* <div className="space-y-1.5">
                            <div className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50">
                                <input
                                    type="text"
                                    value={pushUrl}
                                    readOnly
                                    disabled
                                    className="flex-1 bg-transparent text-sm border-none outline-none text-gray-700"
                                />
                                <button
                                    onClick={handleCopyUrl}
                                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                                    title="Copy URL"
                                >
                                    <Copy className="h-4 w-4 text-sahi-green" />
                                </button>
                            </div>
                        </div> */}
                        <FieldWithIcon
                            key={'justDial'}
                            fieldID={'justDial'}
                            label={''}
                            value={pushUrl}
                            iconTypes={['copy']}
                            // onChange={handleInputChange}
                            disabled={true}
                            readOnly={true}
                        />
                    </div>

                    {/* Footer */}
                    <div className="py-4 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3">
                        <Button
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            className="w-full sm:w-auto"
                        >
                            <X className="w-3 h-3 mr-1" />
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
